import { cssVariablesFor, cssVariablesForUi, themeShape, type TokenMap } from './tokens.ts'; // .ts: this directory is loaded by vite.config.ts — see index.ts

/**
 * THE PRE-PAINT STYLESHEET, GENERATED FROM A PALETTE.
 *
 * Before this file existed the palette lived in three places — `tokens.ts`, a
 * hand-maintained `:root` block in `styles/index.css`, and two hardcoded hexes in the
 * anti-FOUC script in `index.html`. Nothing kept them in sync and no test compared them.
 * That is the "fourth source of truth" failure `tokens.ts` warns about, in the repository
 * that warns about it.
 *
 * This module is PURE and DOM-FREE so `vite.config.ts` can import it at config time. The
 * plugin injects the output into `index.html` at the `<!-- pams:theme -->` marker, in dev,
 * build and preview alike.
 *
 * These rules govern the FIRST PAINT, the no-JavaScript case, and any element outside a
 * ThemeScope. `applyThemeTokens` overwrites them on <html> the moment React mounts —
 * inline styles beat any selector — so there is no ordering hazard.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [amended P5c.0] TWO APPS, TWO PROFILES — AND THE PROFILE IS A REQUIRED PARAMETER
 *  RATHER THAN A DEFAULTED ONE.
 *
 *  The obvious shape was `themeHeadHtml(profile = dashboardThemeCss)`. It is wrong, and
 *  the failure mode is silent: a DEFAULT puts the dashboard's near-black palette in the
 *  OMITTED POSITION of a function the officer app calls. `themeHeadHtmlFrom()` with a
 *  forgotten argument would compile, build, and ship a dark pre-paint block under a light
 *  runtime — a flash of the wrong theme on every cold start that no unit test can see,
 *  because jsdom never loads this output at all.
 *
 *  A default also carries only VALUES. Duty Mitra needs POLICY: emit no `:root.dark`
 *  block, and no boot script whatever. Neither is expressible as a palette.
 *
 *  So the generators take a profile, and the three zero-arity functions `web` already
 *  calls stay exactly as they were, bound to `dashboardThemeCss`. That is also what keeps
 *  `web/src/theme/tokensCss.test.ts`'s "introduces no colour value that is not in
 *  themeTokens" check honest: bound to one profile, it CANNOT see an officer colour.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** One `selector { … }` block of custom properties. */
export type ThemeCssBlock = { readonly selector: string; readonly variables: TokenMap };

export type ThemeCssProfile = {
  /**
   * Selector → variables, in cascade order. ONE entry means a single-theme app, which is
   * what makes Duty Mitra's dark branch unreachable without deleting its values.
   */
  readonly blocks: readonly ThemeCssBlock[];
  /** Theme-invariant `:root{…}` blocks: fonts, control heights, `color-scheme`. */
  readonly invariants: readonly TokenMap[];
  /**
   * The localStorage key the pre-paint script reads.
   *
   * ⚠️ `null` EMITS NO SCRIPT AT ALL, and that is not merely an optimisation. The script
   * exists to choose between two pre-paint blocks; an app with one block has nothing for
   * it to decide. It also hardcoded `'pams.theme'` — the DASHBOARD's key — while the
   * officer app's provider wrote `'pams.app.theme'`, so an officer's stored preference
   * was invisible to it and they got a flash of the wrong theme on every load. Deleting
   * the script for that surface closes the defect rather than moving it.
   */
  readonly storageKey: string | null;
};

const declarationsOf = (variables: TokenMap): string =>
  Object.entries(variables)
    .map(([name, value]) => `${name}:${value}`)
    .join(';');

export const themeFallbackCssFrom = (profile: ThemeCssProfile): string =>
  [
    ...profile.blocks.map(({ selector, variables }) => `${selector}{${declarationsOf(variables)}}`),
    ...profile.invariants.map((variables) => `:root{${declarationsOf(variables)}}`),
  ].join('');

/**
 * The theme half only — light or dark.
 *
 * ⚠️ [amended LIGHT-DEFAULT] IT READS THE STORED CHOICE AND NOTHING ELSE — THE
 * `prefers-color-scheme` ARM IS GONE.
 *
 * A browser with no stored choice paints LIGHT, whatever the operating system is set to, and
 * dark is reached only by an explicit toggle this script can read back under `storageKey`. The
 * OS is not consulted here and is not consulted in `ThemeProvider` either: two readers is how
 * the first paint and the mounted app come to disagree, so BOTH were removed in one pass.
 *
 * ⚠️ THE DEFAULT IS THE ABSENCE OF A BRANCH, WHICH IS WHAT MAKES IT UNCONDITIONAL. `:root` is
 * the light block, so "no stored choice" needs no class, no media query and no fallback value —
 * an operator on a dark laptop gets the same first paint as one on a light laptop.
 *
 * ⚠️ [LIGHT-COLOURFUL] AND THE RETIRED UI-MODE KEY STILL HAS NO READER. A browser carrying
 * `pams.uimode=standard` paints colourful, held by the absence of a reader rather than by a
 * condition.
 *
 * ⚠️ STILL NO COLOUR VALUES. The script adds a CLASS; the `<style>` block carries the values.
 */
export const themeBootScriptFrom = (profile: ThemeCssProfile): string => {
  if (profile.storageKey === null) return '';
  return (
    `(function(){try{var s=localStorage.getItem('${profile.storageKey}');` +
    `if(s==='dark'){` +
    `document.documentElement.classList.add('dark');` +
    `document.documentElement.style.colorScheme='dark';}` +
    `}catch(e){}` +
    `})()`
  );
};

export const themeHeadHtmlFrom = (profile: ThemeCssProfile): string => {
  const boot = themeBootScriptFrom(profile);
  return `<style>${themeFallbackCssFrom(profile)}</style>` + (boot === '' ? '' : `\n    <script>${boot}</script>`);
};

/**
 * THE DASHBOARD'S PROFILE.
 *
 * `:root.dark` is (0,1,1) and beats `:root`'s (0,1,0), so the dark block wins whenever
 * the boot script has added the class — without needing a media query, which would
 * disagree with a stored preference.
 *
 * ⚠️ [amended LIGHT-COLOURFUL] `:root` IS THE COLOURFUL LIGHT PALETTE, AND THERE IS NO
 * `:root.ui-colourful` BLOCK. The monochrome `standard` light palette is no longer offered to
 * anybody, so the class-selected alternate became the base. Dark is untouched: it is still
 * `cssVariablesFor('dark')`, and `uiPalettes.colourful.dark` IS that object referentially.
 */
/**
 * ⚠️ [LIGHT-DEFAULT] THE DASHBOARD'S THEME KEY, DEFINED ONCE.
 *
 * The generated boot script reads it and `web`'s `ThemeProvider` writes it, and they used to hold
 * two independent copies of the literal, with a test comparing the script to a THIRD. A rename
 * would have left the pre-paint script reading a key nobody writes — which does not throw, does not
 * fail a type check, and shows up only as a flash of the wrong theme on every cold load.
 */
export const DASHBOARD_THEME_STORAGE_KEY = 'pams.theme';

export const dashboardThemeCss: ThemeCssProfile = {
  blocks: [
    { selector: ':root', variables: cssVariablesForUi('colourful', 'light') },
    { selector: ':root.dark', variables: cssVariablesFor('dark') },
  ],
  invariants: [
    // Tailwind's preflight compiles to `font-family: var(--default-font-family, …)`, so
    // setting the variable here is enough — and deletes the duplicate stack that used to
    // live in a `body { … }` rule in styles/index.css.
    {
      /*
       * ⚠️ [amended LIGHT-DEFAULT] THE DOCUMENT DECLARES ITS OWN SCHEME, BECAUSE THE META TAG WAS
       * THE LAST READER OF THE OS.
       *
       * `index.html` ships `<meta name="color-scheme" content="light dark">`, which tells the UA
       * the page supports both and therefore to paint NATIVE chrome — scrollbars, form control
       * internals, the caret, autofill — to the operator's system preference. Once light became
       * the default that advertisement had no correct use left: on the dark path the boot script's
       * inline `style.colorScheme='dark'` already decides, and on every light path the meta was
       * the only declaration and it was wrong. The colours were never affected; the native widgets
       * were, for the frames before React's effect writes the same value.
       *
       * ⚠️ A DARK CHOICE IS UNAFFECTED, and the reason must not be "corrected" away: this is a
       * bare `:root{…}` invariant and `:root.dark` declares no scheme at all, so the CSS says
       * light for everybody — but an INLINE style beats every selector, and both the boot script
       * and `applyCssVariables` write `style.colorScheme` on `<html>`.
       *
       * ⚠️ AND IT IS HERE RATHER THAN IN `index.html`, so a theme decision stays in the generated
       * block with the rest of them. `dutyMitraThemeCss` has carried exactly this line since
       * [P5c.0] — the dashboard was the app missing it.
       */
      'color-scheme': 'light',
      '--font-sans': themeShape.fontFamily,
      '--default-font-family': themeShape.fontFamily,
      '--font-mono': themeShape.fontFamilyMono,
      '--default-mono-font-family': themeShape.fontFamilyMono,
    },
    /*
     * THE CONTROL-HEIGHT SCALE, shared between the two component libraries.
     *
     * AntD reads these numbers from `themeShape` through `antdTheme.ts`; shadcn's Button
     * reads them from these variables (`h-[var(--control-height)]`). Without one source a
     * filter row mixes AntD's 38px Select with shadcn's 36px Button, which is a one-pixel
     * misalignment that reads as sloppiness and is invisible in a diff.
     *
     * They live here rather than in `cssVariablesFor` because they do not vary by theme,
     * and a ThemeScope has no reason to redeclare them.
     */
    {
      '--control-height': `${themeShape.controlHeightPx}px`,
      '--control-height-sm': '30px',
      '--control-height-lg': '44px',
    },
  ],
  storageKey: DASHBOARD_THEME_STORAGE_KEY,
};

/** UNCHANGED SIGNATURE, bound to the dashboard. `web`'s tests address these three. */
export const themeFallbackCss = (): string => themeFallbackCssFrom(dashboardThemeCss);

/**
 * The boot script. Class and `color-scheme` only — it carries NO colour values, because
 * the block above already has them and a second copy is the bug this replaces.
 */
export const themeBootScript = (): string => themeBootScriptFrom(dashboardThemeCss);

/** The marker `index.html` carries, and that the Vite plugin refuses to build without. */
export const THEME_MARKER = '<!-- pams:theme -->';

export const themeHeadHtml = (): string => themeHeadHtmlFrom(dashboardThemeCss);
